import React, { useEffect, useState } from "react";
import {
  Paper,
  Chip,
  FormControl,
  Select,
  Input,
  MenuItem,
  Grid,
  Typography,
  InputAdornment,
  TextField,
  Fade,
  Tooltip,
  Avatar,
} from "@mui/material";
import MaterialTable from "@material-table/core";
import moment from "moment";
import "moment/locale/es";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { green } from "@mui/material/colors";

import { getGroups } from "../store/ducks/duckGroups";
import { getLayoutFilters } from "../store/ducks/duckLayoutFilter";
import { getFilters } from "../store/ducks/duckFilter";
import {
  editFilterValueFinancial,
  getFilterValuesFinancial,
  getLayoutsAffectedByFinancialTag,
  getFinancialYears,
  getGroupIdsDefaults,
} from "../store/ducks/duckFilterValueFinancial";
import NavBar from "../components/Navbar";
import { determineFinancialYear, getFormatedDate } from "../utils/financialYear";

moment.locale("es");

const STATUS = {
  CLOSED: "Closed",
  OPEN: "Open",
  BACKLOG: "Backlog",
};

const STATUS_DESCRIPTION = {
  CLOSED: "Closed Period",
  OPEN: "Restricted Open Period",
  BACKLOG: "Pending",
};

const FinancialClosure = () => {
  const dispatch = useDispatch();
  const [year, setYear] = useState("");
  const [loaded, setLoaded] = useState(false);
  const groups = useSelector((state) => state.groups.data).sort((a, b) =>
    a.name !== b.name ? (a.name < b.name ? -1 : 1) : 0,
  );
  const {
    layoutsName: layouts,
    years,
    defaultGroups,
  } = useSelector((state) => state.filterValueFinancial);
  const defaultYear = useSelector((state) => {
    let years = state.filterValueFinancial.years;
    let defaultValue = null;

    if (years.length > 0) {
      defaultValue = years.find((x) => x.value === determineFinancialYear())?.id;
    }

    return defaultValue;
  });

  useEffect(() => {
    if (defaultYear) {
      setYear(defaultYear);
    }
  }, [defaultYear]);

  useEffect(() => {
    if (year && year !== undefined) {
      dispatch(getFilterValuesFinancial(year));
    }
  }, [dispatch, year]);

  useEffect(() => {
    Promise.all([
      dispatch(getGroups()),
      dispatch(getLayoutsAffectedByFinancialTag()),
      dispatch(getLayoutFilters()),
      dispatch(getFilters()),
      dispatch(getFinancialYears()),
      dispatch(getGroupIdsDefaults()),
    ]).then(() => {
      setLoaded(true);
    });
  }, [dispatch]);

  // const { enqueueSnackbar } = useSnackbar();

  const data = useSelector((state) => {
    let pureData = state.filterValueFinancial.data.map((filterValue) => {
      let status = STATUS.CLOSED;
      //Status
      // si es publico es cerrado
      // si no es publico y con grupos es OPEN
      // si no es publico y es futuro es Backlog
      let { /*label, parentValue: yearNumber,*/ public: isPublic, groups } = filterValue;

      status = isPublic
        ? STATUS.CLOSED
        : !isPublic && groups?.length > 0
          ? STATUS.OPEN
          : STATUS.BACKLOG;

      return {
        ...filterValue,
        status,
      };
    });

    return pureData;
  });

  const handleUpdate = (newData, _oldData) => {
    return new Promise((resolve, _) => {
      if (!newData.groups) {
        newData.groups = [];
      }
      /*.then(x => year && dispatch(getFilterValuesFinancial(year))) */
      resolve(dispatch(editFilterValueFinancial(newData, year)));
    });
  };

  const handleYear = (event) => setYear(event.target.value);

  const handleEdit = ({ label, parentValue }) => {
    let maxPastDateAllowed = moment().subtract(3, "months");
    let monthWithPeriod = moment(`${label}-${parentValue}`, "MMMM-YYYY");

    return moment(monthWithPeriod).isAfter(maxPastDateAllowed);
  };

  const columns = [
    {
      title: "Month",
      field: "label",
      align: "justify",
      editable: "never",
      removable: false,
      render: (rowData) => {
        let { label: month, parentValue: year } = rowData;

        return (
          <Grid container alignItems="center" direction="row" spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2">{month}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="textSecondary" variant="caption">
                {year}
              </Typography>
            </Grid>
          </Grid>
        );
      },
    },
    {
      title: "Status",
      field: "status",
      type: "string",
      align: "justify",
      editComponent: (x) => {
        let isDisabled = !handleEdit(x.rowData);

        return (
          <FormControl margin="dense" size="small" variant="outlined">
            <Select
              disabled={isDisabled}
              id="demo-mutiple-chip"
              labelId="demo-mutiple-chip-label"
              margin="dense"
              value={x.value}
              onChange={(e) =>
                x.onRowDataChange({
                  ...x.rowData,
                  status: e.target.value,
                })
              }
            >
              {Object.values(STATUS).map((status, i) => {
                let statusDescriptionKey = status.toUpperCase();

                return (
                  <MenuItem key={`${status}-${i}`} value={status}>
                    {STATUS_DESCRIPTION[statusDescriptionKey]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      },
      render: (rowData) => {
        if (rowData.status === "Closed") {
          return (
            <Tooltip title="Closed">
              <Chip
                color="default"
                icon={<CloseIcon style={{ color: "red" }} />}
                label="Closed Period"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          );
        } else if (rowData.status === "Open") {
          return (
            <Tooltip title="Open">
              <Chip
                color="default"
                icon={<DonutLargeIcon style={{ color: "green" }} />}
                label="Restricted Open Period"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Backlog">
            <Chip
              color="default"
              icon={<EventNoteIcon color="primary" />}
              label="Pending"
              size="small"
              variant="outlined"
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Groups",
      field: "groups",
      editComponent: (x) => {
        let isBacklog = x.rowData.status === STATUS.BACKLOG;
        let isClosed = x.rowData.status === STATUS.CLOSED;
        let isDisabled = (!handleEdit(x.rowData) && x.rowData.public) || isBacklog || isClosed;
        let required = x.rowData.status === STATUS.OPEN;

        return (
          <FormControl margin="dense" variant="outlined">
            <Select
              multiple
              disabled={isDisabled}
              id="demo-mutiple-chip"
              input={<Input id="select-multiple-chip" />}
              labelId="demo-mutiple-chip-label"
              margin="dense"
              renderValue={(selected) => (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={groups?.find((x) => x.id === value).name}
                      style={{
                        margin: 2,
                      }}
                    />
                  ))}
                </div>
              )}
              required={required}
              value={x.value || []}
              onChange={(e) =>
                x.onRowDataChange({
                  ...x.rowData,
                  groups: e.target.value,
                })
              }
            >
              {groups?.map((gr, i) => {
                let { id, name } = gr;

                return (
                  <MenuItem
                    key={`groupI-${id}-${i}`}
                    disabled={defaultGroups.includes(id)}
                    value={id}
                  >
                    {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      },
      render: (rowData) => {
        let status = rowData.status;

        return rowData.groups?.map((groupId, i) => {
          let group = groups.find((g) => g.id === groupId);
          let name = group?.name;

          return group && status !== STATUS.CLOSED ? (
            <Tooltip key={`tooltip-group-${groupId}-${i}`} title={`${name}`}>
              <Chip
                key={`group-chip-${groupId}-${i}`}
                avatar={
                  <Avatar style={{ color: green[700], backgroundColor: green[300] }}>
                    {group && name.slice(0, 1)}
                  </Avatar>
                }
                label={group && name.slice(0, 1)}
                size="small"
                style={{
                  color: green[700],
                  borderColor: green[300],
                  margin: 3,
                }}
                variant="outlined"
              />
            </Tooltip>
          ) : null;
        });
      },
    },
    {
      title: "Modified By",
      field: "modifyOn",
      align: "justify",
      editable: "never",
      removable: false,
      render: (rowData) => {
        let { modifyOn, modifiedBy, id } = rowData;

        return (
          <Tooltip
            key={`tooltip-${id}-${modifiedBy}`}
            title={`Modified on: ${getFormatedDate(modifyOn)}`}
          >
            <Typography key={`${id}-${modifiedBy}`} color="textSecondary" variant="body2">
              {modifiedBy}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <NavBar />
      <Grid container spacing={0} style={{ padding: 10, marginTop: 41 }}>
        <Grid item style={{ padding: 20 }} xs={8}>
          <MaterialTable
            columns={columns}
            components={{
              Container: (props) => (
                <Paper {...props} elevation={2} style={{ paddingRight: 10, paddingLeft: 10 }} />
              ),
              Toolbar: (_props) => (
                <Grid container alignItems="center" spacing={3}>
                  <Grid item xs={3}>
                    <Typography align="center" variant="h6">
                      {"Financial Closure"}
                    </Typography>
                  </Grid>
                  <Grid item xs={7} />
                  <Grid item xs={2}>
                    {years && (
                      <TextField
                        select
                        InputProps={{
                          startAdornment: <InputAdornment position="start">FY</InputAdornment>,
                        }}
                        defaultValue={defaultYear}
                        disabled={years.length <= 0}
                        id="financial-year-select"
                        label="Financial Year"
                        margin="dense"
                        value={year}
                        variant="outlined"
                        onChange={handleYear}
                      >
                        {years &&
                          years.map((y) => (
                            <MenuItem key={`menuItem-year-${y.label}`} value={y.id}>
                              {y.label}
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                  </Grid>
                </Grid>
              ),
            }}
            data={data}
            editable={{
              onRowUpdate: handleUpdate,
              isDeletable: () => false,
              isDeleteHidden: () => true,
              isEditable: handleEdit,
            }}
            isLoading={!loaded}
            options={{
              actionsColumnIndex: -1,
              pageSize: 6,
              pageSizeOptions: [3, 6, 9, 12],
              search: false,
            }}
            title="Financial Closure"
          />
        </Grid>
        <Grid item style={{ padding: 20 }} xs={4}>
          <Fade in={loaded}>
            <Paper style={{ padding: 20 }}>
              <Typography color="secondary" variant="subtitle1">
                Layouts affected
              </Typography>

              <Grid container spacing={1} style={{ margin: "20px 20px 20px 0px" }}>
                {layouts &&
                  layouts.map((lyt, i) => (
                    <Chip
                      key={`Chip-Layout-${i}`}
                      color="default"
                      label={lyt}
                      style={{ margin: 5 }}
                    />
                  ))}
              </Grid>
            </Paper>
          </Fade>
        </Grid>
      </Grid>
    </>
  );
};

export default FinancialClosure;
